import { Injectable, InjectionToken, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApiEndpointKeyType, BdAppConfigResponse } from '@bindoc-projects/app-config-domain-lib';

export const BD_FRONTEND_APP_CONFIG = new InjectionToken<BdAppConfigResponse>(
  'BD_FRONTEND_APP_CONFIG',
);

@Injectable({
  providedIn: 'root',
})
export class BdAppEnvironmentService {
  private _appConfig = inject<BdAppConfigResponse>(BD_FRONTEND_APP_CONFIG);

  public static envStoragePrefix = 'bd.config';

  private readonly _isBrowser: boolean;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this._isBrowser = isPlatformBrowser(platformId);
    this.store();
  }

  public store() {
    if (!this._isBrowser) return;
    this._storeValues(this._appConfig.apiEndpoints);
    this._storeValues(this._appConfig.variables);
  }

  private _storeValues(source: Record<string, string> = {}) {
    const keys = Object.keys(source);

    keys.forEach((key) => {
      const storageKey = `${BdAppEnvironmentService.envStoragePrefix}.${key}`;
      let storageValue: string | object = source[key];

      // due to the fact, kubernetes can only store any env value as string,
      // json objects and arrays are quoted strings.
      // if a quoted string matches the regex, this can be parsed as json again and the 'real' objects returns
      if (
        String(storageValue)
          .match('[\\[\\{][\\\\\\"].*([\\"\\\\]|\\w)[\\]\\}]')
      ) {
        storageValue = JSON.parse(String(storageValue));
      }

      sessionStorage.setItem(storageKey, JSON.stringify(storageValue));
    });
  }

  public load<T = string>(key: ApiEndpointKeyType, optional?: boolean): T;
  /** @deprecated Use ApiEndpointKey constant instead of raw string */
  public load<T = string>(key: string, optional?: boolean): T;
  public load<T = string>(key: string, optional = false): T {
    if (!this._isBrowser) {
      if (!optional) {
        throw new Error(`${key} is missing in storage (server platform).`);
      }
      return null as T;
    }

    const storageKey = `${BdAppEnvironmentService.envStoragePrefix}.${key}`;
    const valueRaw = sessionStorage.getItem(storageKey);

    let result: T | null = null;

    if (!valueRaw && !optional) {
      throw new Error(`${key} is missing in storage.`);
    }

    if (valueRaw) {
      try {
        result = JSON.parse(valueRaw);
      } catch {
        result = valueRaw as T;
      }
    }

    return result as T;
  }
}
